import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Rocket,
  CheckCircle2,
  XCircle,
  Minus,
  Clock,
  GitBranch,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Shield,
} from 'lucide-react';
import { api } from '../services/apiService';
import BranchGraph from '../components/BranchGraph';
import { getAdminProductConfig } from '../config/productConfig';

function StatusBadge({ status, size = 'sm' }: { status: string; size?: 'sm' | 'md' }) {
  const cls = size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  switch (status) {
    case 'success':
      return <CheckCircle2 className={`${cls} text-green-400`} />;
    case 'failure':
      return <XCircle className={`${cls} text-red-400`} />;
    case 'in_progress':
    case 'queued':
      return <RubyGemMark size={size === 'md' ? 20 : 16} drawing />;
    case 'cancelled':
      return <Minus className={`${cls} text-stone-500`} />;
    default:
      return <Minus className={`${cls} text-stone-600`} />;
  }
}

function timeAgo(dateStr: string) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function duration(start: string, end?: string) {
  if (!start) return '--';
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const diff = Math.floor((e - s) / 1000);
  const m = Math.floor(diff / 60);
  const sec = diff % 60;
  return `${m}m ${sec}s`;
}

function RepoDetail() {
  const { owner, repo } = useParams<{ owner: string; repo: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'deployments' | 'branches'>('deployments');
  const [runs, setRuns] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [graphData, setGraphData] = useState<any>(null);
  const [expandedRun, setExpandedRun] = useState<number | null>(null);
  const [runJobs, setRunJobs] = useState<Record<number, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registry, setRegistry] = useState<any>(null);
  const [deploying, setDeploying] = useState(false);
  const [deployEnv, setDeployEnv] = useState<'dev' | 'prod'>('dev');
  const [showDeployModal, setShowDeployModal] = useState(false);

  const fetchData = useCallback(async () => {
    if (!owner || !repo) return;
    setLoading(true);
    setError('');
    try {
      const detail = await api.devops.getRepoDetail(owner, repo) as any;
      setRuns(detail.runs || []);
      setRegistry(detail.registry);

      if (activeTab === 'branches') {
        const [branchData, graph] = await Promise.all([
          api.devops.listBranches(owner, repo),
          api.devops.getBranchGraph(owner, repo),
        ]) as [any, any];
        setBranches(branchData.branches || []);
        setGraphData(graph);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load repo detail');
    } finally {
      setLoading(false);
    }
  }, [owner, repo, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadRunJobs = async (runId: number) => {
    if (runJobs[runId]) {
      setExpandedRun(expandedRun === runId ? null : runId);
      return;
    }
    if (!owner || !repo) return;
    try {
      const data = await api.devops.getRunDetails(owner, repo, runId) as any;
      setRunJobs(prev => ({ ...prev, [runId]: data.jobs || [] }));
      setExpandedRun(runId);
    } catch (err: any) {
      console.error('Failed to load run jobs:', err);
    }
  };

  const handleDeploy = async () => {
    if (!owner || !repo) return;
    setDeploying(true);
    setShowDeployModal(false);
    try {
      await api.devops.triggerDeploy(owner, repo, { product: getAdminProductConfig().id, environment: deployEnv });
      setTimeout(fetchData, 3000);
    } catch (err: any) {
      setError(`Deploy failed: ${err.message}`);
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/deployments')}
            className="p-2 text-stone-400 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {registry?.displayName || repo}
            </h1>
            <p className="text-sm text-stone-400">
              {owner}/{repo}
              {registry && (
                <span className="ml-2 text-xs bg-stone-100 px-2 py-0.5 rounded">
                  {registry.category} · {registry.productId}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setDeployEnv('dev');
              setShowDeployModal(true);
            }}
            disabled={deploying}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {deploying ? <RubyGemMark size={16} drawing color="currentColor" /> : <Rocket className="w-4 h-4" />}
            Deploy
          </button>
          <a
            href={`https://github.com/${owner}/${repo}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-stone-400 hover:text-gray-900 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
          </a>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-stone-400 hover:text-gray-900 transition-colors"
          >
            {loading ? <RubyGemMark size={20} drawing color="currentColor" /> : <RefreshCw className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-stone-200 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('deployments')}
          className={`px-4 py-2 text-sm rounded-md transition-colors ${
            activeTab === 'deployments'
              ? 'bg-admin-primary text-white'
              : 'text-stone-400 hover:text-gray-900'
          }`}
        >
          Deployments
        </button>
        <button
          onClick={() => setActiveTab('branches')}
          className={`px-4 py-2 text-sm rounded-md transition-colors ${
            activeTab === 'branches'
              ? 'bg-admin-primary text-white'
              : 'text-stone-400 hover:text-gray-900'
          }`}
        >
          Branches
        </button>
      </div>

      {loading && (
        <RubyLoader variant="page" />
      )}

      {/* Deployments Tab */}
      {!loading && activeTab === 'deployments' && (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          {runs.length === 0 ? (
            <div className="px-4 py-12 text-center text-stone-500">
              No workflow runs found
            </div>
          ) : (
            <div>
              {/* Table Header */}
              <div className="grid grid-cols-[32px_1fr_120px_100px_100px_100px] gap-2 px-4 py-2 text-xs text-stone-500 uppercase tracking-wider border-b border-stone-200">
                <span></span>
                <span>Workflow</span>
                <span>Branch</span>
                <span className="text-center">Status</span>
                <span className="text-center">Duration</span>
                <span className="text-right">Time</span>
              </div>

              {runs.map((run: any) => (
                <div key={run.id}>
                  <button
                    onClick={() => loadRunJobs(run.id)}
                    className="w-full grid grid-cols-[32px_1fr_120px_100px_100px_100px] gap-2 px-4 py-3 hover:bg-stone-100 transition-colors items-center border-b border-stone-200/30 text-left"
                  >
                    <div>
                      {expandedRun === run.id ? (
                        <ChevronDown className="w-4 h-4 text-stone-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-stone-500" />
                      )}
                    </div>
                    <div className="truncate">
                      <span className="text-sm text-gray-900">{run.name}</span>
                      <span className="text-xs text-stone-500 ml-2">#{run.run_number}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <GitBranch className="w-3 h-3 text-stone-500" />
                      <span className="text-xs text-stone-400 truncate">{run.head_branch}</span>
                    </div>
                    <div className="flex justify-center">
                      <StatusBadge status={run.conclusion || run.status} />
                    </div>
                    <div className="text-center text-xs text-stone-400">
                      {duration(run.run_started_at || run.created_at, run.updated_at)}
                    </div>
                    <div className="text-right text-xs text-stone-500">
                      {timeAgo(run.created_at)}
                    </div>
                  </button>

                  {/* Expanded Run Details (Jobs + Steps) */}
                  {expandedRun === run.id && runJobs[run.id] && (
                    <div className="bg-stone-100/50 border-b border-stone-200/30 px-8 py-3">
                      {runJobs[run.id].map((job: any) => (
                        <div key={job.id} className="mb-3 last:mb-0">
                          <div className="flex items-center gap-2 mb-1">
                            <StatusBadge status={job.conclusion || job.status} />
                            <span className="text-sm text-gray-900 font-medium">{job.name}</span>
                            <span className="text-xs text-stone-500">
                              {duration(job.started_at, job.completed_at)}
                            </span>
                          </div>
                          <div className="ml-6 space-y-0.5">
                            {(job.steps || []).map((step: any) => (
                              <div key={step.number} className="flex items-center gap-2">
                                <StatusBadge status={step.conclusion || step.status} />
                                <span className="text-xs text-stone-400">{step.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Branches Tab */}
      {!loading && activeTab === 'branches' && (
        <div className="space-y-6">
          {/* Branch Graph */}
          {graphData && (
            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <h3 className="text-sm font-medium text-stone-600 mb-3">Commit Graph</h3>
              <BranchGraph data={graphData} />
            </div>
          )}

          {/* Branch List */}
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2 text-xs text-stone-500 uppercase tracking-wider border-b border-stone-200">
              Branches ({branches.length})
            </div>
            {branches.map((branch: any) => (
              <div
                key={branch.name}
                className="flex items-center justify-between px-4 py-3 border-b border-stone-200/30 last:border-b-0"
              >
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-stone-500" />
                  <span className="text-sm text-gray-900">{branch.name}</span>
                  {branch.protected && (
                    <span title="Protected"><Shield className="w-3 h-3 text-yellow-500" /></span>
                  )}
                </div>
                <span className="text-xs text-stone-500 font-mono">{branch.sha?.slice(0, 7)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deploy Modal */}
      {showDeployModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Deploy {registry?.displayName || repo}
            </h3>
            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-3 p-3 bg-stone-100 rounded-lg cursor-pointer border border-stone-200 hover:border-green-600">
                <input
                  type="radio"
                  name="env"
                  value="dev"
                  checked={deployEnv === 'dev'}
                  onChange={() => setDeployEnv('dev')}
                  className="accent-green-500"
                />
                <div>
                  <p className="text-gray-900 text-sm font-medium">Deploy to Dev</p>
                  <p className="text-stone-500 text-xs">development branch</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-stone-100 rounded-lg cursor-pointer border border-stone-200 hover:border-red-600">
                <input
                  type="radio"
                  name="env"
                  value="prod"
                  checked={deployEnv === 'prod'}
                  onChange={() => setDeployEnv('prod')}
                  className="accent-red-500"
                />
                <div>
                  <p className="text-gray-900 text-sm font-medium">Deploy to Production</p>
                  <p className="text-stone-500 text-xs">main branch + confirm</p>
                </div>
              </label>
            </div>
            {deployEnv === 'prod' && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 mb-4 text-red-300 text-xs">
                Production deployment requires main branch to be up to date.
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeployModal(false)}
                className="flex-1 py-2 bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeploy}
                className={`flex-1 py-2 text-white rounded-lg ${
                  deployEnv === 'prod' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                Deploy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RepoDetail;
